import * as React from "react"

import { cn } from "@/lib/utils"

const labelCells = (table) => {
  const heads = Array.from(table.querySelectorAll(":scope > thead th")).map((th) => th.textContent.trim());
  table.querySelectorAll(":scope > tbody > tr").forEach((tr) => {
    const first = tr.children[0];
    const lead = tr.children.length > 2 && first && first.textContent.trim().length <= 3;
    if (lead) tr.setAttribute("data-lead", "true");
    else tr.removeAttribute("data-lead");
    let idx = 0;
    Array.from(tr.children).forEach((td) => {
      const span = Number(td.getAttribute("colspan") || 1);
      if (span > 1 && span >= heads.length - 1) td.setAttribute("data-full", "true");
      else td.removeAttribute("data-full");
      const label = heads[idx] || "";
      if (td.getAttribute("data-label") !== label) td.setAttribute("data-label", label);
      idx += span;
    });
  });
};

const Table = React.forwardRef(({ className, mobileCards = true, ...props }, ref) => {
  const inner = React.useRef(null);
  React.useImperativeHandle(ref, () => inner.current);
  React.useEffect(() => {
    const el = inner.current;
    if (!el || !mobileCards) return undefined;
    labelCells(el);
    const obs = new MutationObserver(() => labelCells(el));
    obs.observe(el, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [mobileCards]);
  return (
    <div className="relative w-full overflow-auto">
      <table
        ref={inner}
        className={cn("w-full caption-bottom text-sm", mobileCards && "rtable", className)}
        {...props} />
    </div>
  );
})
Table.displayName = "Table"

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b [&_tr]:border-[#ECECEE] [&_tr:hover]:bg-transparent", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props} />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props} />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b border-[#F0F0F2] transition-colors hover:bg-white data-[state=selected]:bg-white",
      className
    )}
    {...props} />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-3 first:pl-6 last:pr-6 text-left align-middle text-xs font-normal text-slate-400 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "px-3 py-3 first:pl-6 last:pr-6 align-middle text-slate-800 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props} />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
